import React, { useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/* ══════════════════════════════════════════════════════════════
   JailBarHero — legendary edition
   • Cursor-tracking spotlight
   • Ken Burns slow zoom on bg
   • Bronze ember particles + fine dust drift
   • Bar micro-rattle before scroll unlocks
   • Word-by-word title reveal with rotate/skew
   • Scroll-driven parallax unlock (unchanged behavior)
   ══════════════════════════════════════════════════════════════ */
const BARS_PER_SIDE = 6;
const EMBER_COUNT = 22;
const DUST_COUNT = 40;

const splitWords = (input) => {
  if (input == null) return null;
  if (typeof input !== 'string') return input;
  return input.split(/(\s+)/).map((chunk, i) => {
    if (/^\s+$/.test(chunk)) return chunk;
    return <span key={i} className="word">{chunk}</span>;
  });
};

const JailBarHero = ({
  bgImage, badge, title, titleAccent, subtitle, ctas,
  scrollHintText = 'SCROLL',
  testId = 'jail-bar-hero',
}) => {
  const sectionRef = useRef(null);
  const bgImgRef = useRef(null);
  const overlayRef = useRef(null);
  const leftBarsRef = useRef(null);
  const rightBarsRef = useRef(null);
  const leftInnerRef = useRef(null);
  const rightInnerRef = useRef(null);
  const lockRef = useRef(null);
  const spotlightRef = useRef(null);
  const [reduced, setReduced] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);

    const ctx = gsap.context(() => {
      // Word-by-word title reveal (both eng & accent)
      const words = sectionRef.current.querySelectorAll('.jbh__title .word, .jbh__title-accent .word');
      if (mq.matches) {
        words.forEach((w) => w.classList.add('in'));
      } else {
        gsap.set(words, { opacity: 0 });
        words.forEach((w, i) => {
          gsap.delayedCall(0.35 + i * 0.09, () => w.classList.add('in'));
        });
      }

      if (mq.matches) {
        gsap.set([leftBarsRef.current], { x: '-102%' });
        gsap.set([rightBarsRef.current], { x: '102%' });
        gsap.set(lockRef.current, { opacity: 0, scale: 0.7 });
        gsap.set(bgImgRef.current, { filter: 'brightness(0.85) saturate(1) contrast(1.02)' });
        gsap.set(overlayRef.current, { opacity: 0.55 });
        gsap.fromTo(sectionRef.current, { opacity: 0 }, { opacity: 1, duration: 1.1, ease: 'power2.out' });
        return;
      }

      /* Initial locked state */
      gsap.set(leftBarsRef.current, { x: '0%' });
      gsap.set(rightBarsRef.current, { x: '0%' });
      gsap.set(lockRef.current, { opacity: 1, scale: 1 });
      gsap.set(bgImgRef.current, { scale: 1.05 });

      /* Ken Burns slow zoom on bg (independent of scroll) */
      gsap.to(bgImgRef.current, {
        scale: 1.13,
        duration: 22,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });

      /* Scroll-driven "Freedom" unlock */
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 0.4,
          onUpdate: (self) => {
            if (self.progress > 0.02 && !scrolled) setScrolled(true);
          },
        },
      });
      tl.to(lockRef.current, { opacity: 0, scale: 0.55, duration: 0.22 }, 0)
        .to(leftBarsRef.current, { x: '-102%', duration: 1, ease: 'none' }, 0.06)
        .to(rightBarsRef.current, { x: '102%', duration: 1, ease: 'none' }, 0.06)
        .to(bgImgRef.current, { filter: 'brightness(0.95) saturate(1.05) contrast(1.04)', duration: 1, ease: 'none' }, 0)
        .to(overlayRef.current, { opacity: 0.35, duration: 1, ease: 'none' }, 0);

      /* Pointer parallax + cursor spotlight (fine pointer only) */
      const canHover = window.matchMedia('(pointer: fine)').matches;
      if (canHover) {
        const leftX = gsap.quickTo(leftInnerRef.current, 'x', { duration: 0.7, ease: 'power3' });
        const rightX = gsap.quickTo(rightInnerRef.current, 'x', { duration: 0.7, ease: 'power3' });
        const leftY = gsap.quickTo(leftInnerRef.current, 'y', { duration: 0.7, ease: 'power3' });
        const rightY = gsap.quickTo(rightInnerRef.current, 'y', { duration: 0.7, ease: 'power3' });
        const onMove = (e) => {
          const rect = sectionRef.current.getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * 100;
          const py = ((e.clientY - rect.top) / rect.height) * 100;
          const nx = (e.clientX / window.innerWidth - 0.5) * 2;
          const ny = (e.clientY / window.innerHeight - 0.5) * 2;
          leftX(nx * -8);
          rightX(nx * -8);
          leftY(ny * 5);
          rightY(ny * 5);
          if (spotlightRef.current) {
            spotlightRef.current.style.setProperty('--mx', `${px}%`);
            spotlightRef.current.style.setProperty('--my', `${py}%`);
          }
        };
        sectionRef.current.addEventListener('mousemove', onMove);
        return () => sectionRef.current?.removeEventListener('mousemove', onMove);
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const bars = () => Array.from({ length: BARS_PER_SIDE }, (_, i) => (
    <div key={i} className={`jbh__bar ${i === 2 ? 'jbh__bar--accent' : ''}`} />
  ));

  return (
    <section className="jbh" ref={sectionRef} data-testid={testId} aria-label="Hero">
      <div className="jbh__bg">
        <img ref={bgImgRef} src={bgImage} alt="Detention environment" loading="eager" />
      </div>
      <div className="jbh__overlay" ref={overlayRef} />
      <div className="jbh__vignette" />

      <div className="jbh__depth-bars jbh__depth-bars--left" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, i) => <div key={i} className="jbh__depth-bar" />)}
      </div>
      <div className="jbh__depth-bars jbh__depth-bars--right" aria-hidden="true">
        {Array.from({ length: 5 }).map((_, i) => <div key={i} className="jbh__depth-bar" />)}
      </div>
      <div className="jbh__lightrays" aria-hidden="true" />
      <div className="jbh__vignette-pulse" aria-hidden="true" />

      {/* ── Ember particles (bronze rising, warm) ── */}
      <div className="jbh__embers" aria-hidden="true">
        {Array.from({ length: EMBER_COUNT }).map((_, i) => {
          const size = i % 5 === 0 ? 'jbh__ember--big' : i % 3 === 0 ? 'jbh__ember--tiny' : '';
          const drift = (Math.sin(i * 1.7) * 60).toFixed(0) + 'px';
          const dur = 12 + (i % 7) * 3;
          const delay = (i * 0.9) % 14;
          return (
            <span
              key={i}
              className={`jbh__ember ${size}`}
              style={{
                left: `${(i * 4.7) % 100}%`,
                animationDuration: `${dur}s`,
                animationDelay: `${delay}s`,
                '--drift': drift,
              }}
            />
          );
        })}
      </div>

      {/* ── Fine dust drift (back layer) ── */}
      <div className="jbh__dust" aria-hidden="true">
        {Array.from({ length: DUST_COUNT }).map((_, i) => (
          <span
            key={i}
            style={{
              left: `${(i * 2.6) % 100}%`,
              animationDuration: `${18 + (i % 9) * 2}s`,
              animationDelay: `${(i * 0.5) % 20}s`,
              '--dx': `${(Math.cos(i * 0.9) * 80).toFixed(0)}px`,
            }}
          />
        ))}
      </div>

      {/* ── Cursor spotlight ── */}
      <div className="jbh__spotlight" ref={spotlightRef} aria-hidden="true" />

      <div className={`jbh__bars jbh__bars--left ${!scrolled && !reduced ? 'jbh__bars--pre-rattle' : ''}`} ref={leftBarsRef} data-testid="jbh-bars-left">
        <div className="jbh__bars-inner" ref={leftInnerRef}>{bars()}</div>
      </div>
      <div className={`jbh__bars jbh__bars--right ${!scrolled && !reduced ? 'jbh__bars--pre-rattle' : ''}`} ref={rightBarsRef} data-testid="jbh-bars-right">
        <div className="jbh__bars-inner" ref={rightInnerRef}>{bars()}</div>
      </div>

      {!reduced && (
        <div className="jbh__lock" ref={lockRef} data-testid="jbh-lock">
          <Lock size={22} />
          <span className="jbh__lock-label">SECURED</span>
        </div>
      )}

      <div className="jbh__content">
        {badge && <div className="jbh__badge">{badge}</div>}
        <h1 className="jbh__title" data-testid="jbh-title">
          {splitWords(title)}
          {titleAccent && (
            <>
              {' '}
              <span className="jbh__title-accent">{splitWords(titleAccent)}</span>
            </>
          )}
        </h1>
        {subtitle && <p className="jbh__subtitle">{subtitle}</p>}
        {ctas && <div className="jbh__ctas">{ctas}</div>}
      </div>

      <div className="jbh__scroll-hint">
        <span>{scrollHintText}</span>
        <div className="jbh__scroll-hint-line" />
      </div>
    </section>
  );
};

export default JailBarHero;
